import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PapiUser } from '@wix/da-papi-types';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';

import {
  BiData,
  BiContextProvidedValue,
  MessageIconClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useRestrictToLoggedInAndVerified } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedInAndVerified';
import { toggleStartNewMessage } from '@wix/da-damz-shared/pkg/redux/actions/room';
import { addDraftUser } from '@wix/da-damz-shared/pkg/redux/actions/drafts';
import { TEMP_DRAFT_ROOM_ID } from '@wix/da-damz-shared/pkg/constants';
import { openDuperbrowse } from '@wix/da-shared-react/pkg/Duperbrowse/redux/actionCreators';
import {
  DAMZ_PAGE_DUPERBROWSE_ITEMID,
  DAMZ_PAGE_DUPERBROWSE_STREAMID,
} from '@wix/da-shared-react/pkg/Duperbrowse/redux/constants';
import { DuperbrowseSimpleStream } from '@wix/da-shared-react/pkg/Duperbrowse/redux/types';

interface Props {
  className?: string;
  profileOwner: PapiUser;
  isContactable?: boolean;
  iconOnly?: boolean;
}

export const SendMessageButton: FC<Props> = ({
  className,
  profileOwner,
  isContactable = true,
  iconOnly = false,
}) => {
  const { restrictToLoggedInAndVerified, isLoggedIn, isVerificationNeeded } =
    useRestrictToLoggedInAndVerified();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleClick = restrictToLoggedInAndVerified(
    ReferralType.MESSAGES,
    'messages',
    undefined
  );

  const handleDamzClick = useCallback(() => {
    dispatch(toggleStartNewMessage());
    dispatch(addDraftUser(profileOwner, TEMP_DRAFT_ROOM_ID));
    dispatch(
      openDuperbrowse(DAMZ_PAGE_DUPERBROWSE_ITEMID, {
        streamId: DAMZ_PAGE_DUPERBROWSE_STREAMID,
        items: [DAMZ_PAGE_DUPERBROWSE_ITEMID],
      } as DuperbrowseSimpleStream)
    );
  }, [dispatch, profileOwner]);

  const shouldBlockAction = isVerificationNeeded || !isLoggedIn;

  const biData = BiData<MessageIconClickBiEvent>({
    evid: 470,
    recipientid: profileOwner.useridUuid,
    sectionname: BiContextProvidedValue,
  });

  if (iconOnly) {
    return (
      <IconButton
        size="medium"
        className={className}
        tooltipText={t(`navigation.subnavigation.sendMessage`)}
        icon={MailIcon}
        onClick={shouldBlockAction ? handleClick : handleDamzClick}
        aria-label={t('navigation.subnavigation.sendMessage')}
        biData={biData}
      />
    );
  }

  return (
    <GhostButton
      size="medium"
      className={className}
      variant="main"
      onClick={shouldBlockAction ? handleClick : handleDamzClick}
      aria-label={t('navigation.subnavigation.sendMessage')}
      biData={biData}
    >
      {t(`navigation.subnavigation.sendMessage`)}
    </GhostButton>
  );
};
SendMessageButton.displayName = 'SendMessageButton';

export default SendMessageButton;
